import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

type Mode = 'uniform' | 'accelerated' | 'freefall'
interface Props { title?: string; description?: string; onComplete?: () => void; mode?: Mode }

// Đồ thị chuyển động (Lớp 10): độ dịch chuyển d, vận tốc v, gia tốc a theo thời gian.
//  - uniform (Bài 4): đều → d bậc nhất, v hằng, a = 0
//  - accelerated (Bài 5): biến đổi đều → v bậc nhất, d parabol, a hằng
//  - freefall (Bài 6): rơi tự do → a = g
const TOPW = 660, TOPH = 100, GW = 660, GH = 320

const MotionGraphsSimulation: React.FC<Props> = ({
  title = 'Đồ thị chuyển động',
  description = 'Quan sát đồng thời độ dịch chuyển d, vận tốc v, gia tốc a theo thời gian. Bật/tắt từng đồ thị.',
  onComplete,
  mode = 'accelerated',
}) => {
  const top = useRef<HTMLCanvasElement>(null)
  const gcv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const [a0, setA0] = useState(mode === 'freefall' ? 9.8 : 2)
  const [v0, setV0] = useState(mode === 'uniform' ? 5 : mode === 'freefall' ? 0 : 1)
  const [showD, setShowD] = useState(true)
  const [showV, setShowV] = useState(mode !== 'uniform')
  const [showA, setShowA] = useState(mode !== 'uniform')
  const [running, setRunning] = useState(true)
  const a0Ref = useRef(a0), v0Ref = useRef(v0), runRef = useRef(running)
  const sdRef = useRef(showD), svRef = useRef(showV), saRef = useRef(showA)
  const firstRef = useRef(false)
  useEffect(() => { a0Ref.current = a0 }, [a0])
  useEffect(() => { v0Ref.current = v0 }, [v0])
  useEffect(() => { runRef.current = running }, [running])
  useEffect(() => { sdRef.current = showD }, [showD])
  useEffect(() => { svRef.current = showV }, [showV])
  useEffect(() => { saRef.current = showA }, [showA])
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const T = mode === 'freefall' ? 3 : 5
  const acc = () => (mode === 'uniform' ? 0 : a0Ref.current)
  const vel = (t: number) => v0Ref.current + acc() * t
  const dis = (t: number) => v0Ref.current * t + 0.5 * acc() * t * t
  const maxAbs = (f: (t: number) => number) => { let m = 0; for (let i = 0; i <= 40; i++) { const v = Math.abs(f((T * i) / 40)); if (v > m) m = v } return m }
  const niceMax = (m: number) => { m = Math.max(0.5, m); const p = Math.pow(10, Math.floor(Math.log10(m))); const n = m / p; const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10; return step * p }
  const fmtNum = (n: number) => { const r = Math.round(n * 10) / 10; return Number.isInteger(r) ? String(r) : r.toFixed(1) }

  const drawTop = useCallback(() => {
    const ctx = prepCanvas(top.current, TOPW, TOPH); if (!ctx) return
    ctx.clearRect(0, 0, TOPW, TOPH)
    const t = tRef.current, dscale = Math.max(1, maxAbs(dis))
    const frac = Math.max(-1, Math.min(1, dis(t) / dscale))
    if (mode === 'freefall') {
      const x = 80, y0 = 14, y1 = TOPH - 16
      ctx.strokeStyle = '#cbd5e1'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(x, y0); ctx.lineTo(x, y1); ctx.stroke()
      const y = y0 + frac * (y1 - y0)
      ctx.fillStyle = '#2563eb'; ctx.beginPath(); ctx.arc(x, y, 11, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#334155'; ctx.font = 'bold 12px sans-serif'; ctx.fillText('Rơi tự do (g = 9,8 m/s²)', 110, TOPH / 2)
    } else {
      const xL = 30, xR = TOPW - 30, y = TOPH / 2, Ox = (xL + xR) / 2, half = (xR - xL) / 2
      ctx.fillStyle = '#e2e8f0'; ctx.fillRect(xL, y + 14, xR - xL, 5)
      ctx.strokeStyle = '#cbd5e1'; ctx.setLineDash([3, 3]); ctx.beginPath(); ctx.moveTo(Ox, y - 24); ctx.lineTo(Ox, y + 24); ctx.stroke(); ctx.setLineDash([])
      ctx.fillStyle = '#94a3b8'; ctx.font = '10px sans-serif'; ctx.fillText('O', Ox - 3, y + 34)
      const x = Ox + frac * half * 0.9
      ctx.fillStyle = '#2563eb'; ctx.beginPath(); ctx.arc(x, y, 12, 0, Math.PI * 2); ctx.fill()
      // mũi tên vận tốc — GỐC ĐẶT LÊN VẬT (tâm vật)
      const vNow = vel(t), dir = vNow < 0 ? -1 : 1, vlen = Math.min(80, (Math.abs(vNow) / Math.max(1, Math.abs(vel(T)), Math.abs(v0Ref.current))) * 80)
      if (vlen > 2) {
        const ex = x + dir * vlen
        ctx.strokeStyle = '#16a34a'; ctx.fillStyle = '#16a34a'; ctx.lineWidth = 3; ctx.lineCap = 'round'
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, y); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(ex, y); ctx.lineTo(ex - dir * 7, y - 4); ctx.lineTo(ex - dir * 7, y + 4); ctx.closePath(); ctx.fill()
        ctx.lineCap = 'butt'
        ctx.fillStyle = '#15803d'; ctx.font = 'bold italic 12px sans-serif'; ctx.fillText('v', x + dir * vlen / 2 - 3, y - 6)
      }
    }
    ctx.fillStyle = '#64748b'; ctx.font = '12px sans-serif'; ctx.fillText(`t = ${t.toFixed(1)} s`, 8, 14)
  }, [mode])

  const drawGraphs = useCallback(() => {
    const ctx = prepCanvas(gcv.current, GW, GH); if (!ctx) return
    ctx.clearRect(0, 0, GW, GH)
    const t = tRef.current
    const bands: { label: string; color: string; f: (t: number) => number; vmax: number; unit: string }[] = []
    if (sdRef.current) bands.push({ label: 'd', color: '#2563eb', f: dis, vmax: niceMax(maxAbs(dis)), unit: 'm' })
    if (svRef.current) bands.push({ label: 'v', color: '#16a34a', f: vel, vmax: niceMax(maxAbs(vel)), unit: 'm/s' })
    if (saRef.current) bands.push({ label: 'a', color: '#dc2626', f: () => acc(), vmax: niceMax(Math.abs(acc())), unit: 'm/s²' })
    if (!bands.length) { ctx.fillStyle = '#94a3b8'; ctx.font = '13px sans-serif'; ctx.fillText('Bật ít nhất một đồ thị (d, v, a)', 20, GH / 2); return }
    const bandH = GH / bands.length, x0 = 58, x1 = GW - 14
    bands.forEach((bnd, bi) => {
      const top0 = bi * bandH + 14, bot = (bi + 1) * bandH - 16, midY = (top0 + bot) / 2, halfH = (bot - top0) / 2
      const X = (tt: number) => x0 + (tt / T) * (x1 - x0)
      const Y = (val: number) => midY - (val / bnd.vmax) * halfH * 0.92
      // ô lưới rõ ràng: dọc mỗi 1 s; ngang ±vmax/2, ±vmax (có nhãn giá trị, hiện cả phía âm)
      ctx.font = '9px sans-serif'
      ctx.strokeStyle = '#e6ebf1'; ctx.lineWidth = 1
      for (let s = 1; s <= T; s++) { ctx.beginPath(); ctx.moveTo(X(s), top0); ctx.lineTo(X(s), bot); ctx.stroke() }
      ctx.textAlign = 'right'
      for (const lv of [bnd.vmax, bnd.vmax / 2, -bnd.vmax / 2, -bnd.vmax]) {
        ctx.strokeStyle = '#e6ebf1'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x0, Y(lv)); ctx.lineTo(x1, Y(lv)); ctx.stroke()
        ctx.fillStyle = '#94a3b8'; ctx.fillText(fmtNum(lv), x0 - 5, Y(lv) + 3)
      }
      ctx.textAlign = 'left'
      // trục: t (ngang tại 0, giữa) + value (dọc) + mũi tên
      ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 1.8; ctx.lineJoin = 'round'
      ctx.beginPath(); ctx.moveTo(x0, top0 - 2); ctx.lineTo(x0, bot + 2); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(x0, midY); ctx.lineTo(x1, midY); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(x0, top0 - 7); ctx.lineTo(x0 - 4, top0 + 1); ctx.lineTo(x0 + 4, top0 + 1); ctx.closePath(); ctx.fill()
      ctx.beginPath(); ctx.moveTo(x1 + 5, midY); ctx.lineTo(x1 - 3, midY - 4); ctx.lineTo(x1 - 3, midY + 4); ctx.closePath(); ctx.fill()
      ctx.font = 'bold 12px sans-serif'; ctx.fillStyle = bnd.color; ctx.fillText(`${bnd.label} (${bnd.unit})`, x0 + 4, top0 + 8)
      ctx.fillStyle = '#94a3b8'; ctx.font = '10px sans-serif'; ctx.fillText('t (s)', x1 - 22, midY - 5); ctx.fillStyle = '#64748b'; ctx.fillText('0', x0 - 12, midY + 11)
      // đường cong tới t hiện tại
      ctx.strokeStyle = bnd.color; ctx.lineWidth = 2.5; ctx.beginPath()
      for (let i = 0; i <= 80; i++) { const tt = (t * i) / 80; const px = X(tt), py = Y(bnd.f(tt)); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) }
      ctx.stroke()
      ctx.fillStyle = bnd.color; ctx.beginPath(); ctx.arc(X(t), Y(bnd.f(t)), 4, 0, Math.PI * 2); ctx.fill()
    })
  }, [mode])

  const tick = useCallback(() => {
    if (runRef.current && tRef.current < T) { tRef.current = Math.min(T, tRef.current + 0.03); if (tRef.current >= T) setRunning(false) }
    drawTop(); drawGraphs(); raf.current = requestAnimationFrame(tick)
  }, [drawTop, drawGraphs, T])
  useEffect(() => { raf.current = requestAnimationFrame(tick); return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [tick])

  const Chk = ({ on, set, label, color }: { on: boolean; set: (b: boolean) => void; label: string; color: string }) => (
    <label className="flex items-center gap-1.5 cursor-pointer select-none"><input type="checkbox" checked={on} onChange={(e) => { touch(); set(e.target.checked) }} className="w-4 h-4" style={{ accentColor: color }} /><span style={{ color }} className="font-bold">{label}</span></label>
  )

  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={top} width={TOPW} height={TOPH} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <canvas ref={gcv} width={GW} height={GH} className="w-full mt-2 rounded-lg border border-gray-100 bg-slate-50" />
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm">
        <span className="text-gray-600">Hiện đồ thị:</span>
        <Chk on={showD} set={setShowD} label="d (độ dịch chuyển)" color="#2563eb" />
        <Chk on={showV} set={setShowV} label="v (vận tốc)" color="#16a34a" />
        <Chk on={showA} set={setShowA} label="a (gia tốc)" color="#dc2626" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-2 text-sm">
        {mode !== 'freefall' && <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Vận tốc đầu v₀</span><input type="range" min={0} max={10} step={0.5} value={v0} onChange={(e) => { touch(); setV0(Number(e.target.value)) }} className="flex-1" /><span className="w-14 text-right font-semibold">{v0} m/s</span></label>}
        {mode === 'accelerated' && <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Gia tốc a</span><input type="range" min={-4} max={4} step={0.5} value={a0} onChange={(e) => { touch(); setA0(Number(e.target.value)) }} className="flex-1" /><span className="w-14 text-right font-semibold">{a0} m/s²</span></label>}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { touch(); if (tRef.current >= T) tRef.current = 0; setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700">{running ? <><Pause className="w-4 h-4" /> Dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        <button onClick={() => { tRef.current = 0 }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Lặp lại</button>
      </div>
    </div>
  )
}
export default MotionGraphsSimulation
